"use client";

import React, { useState } from "react";
import type { Route } from "next";
import Link from "next/link";
import * as Headless from "@headlessui/react";

import { cn } from "@rivval/css";
import * as Icons from "@rivval/icons";
import { RIVVALLogo, TextIcon, XIcon } from "@rivval/icons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@rivval/ui/accordion";

type IconName = keyof typeof Icons;
type IconsType = Record<IconName, React.ComponentType<{ className?: string }>>;

interface MobileNavLink {
  href: Route;
  title: string;
  subNav?: {
    href: Route;
    title: string;
  }[];
}

interface MobileNavProps extends React.HTMLAttributes<HTMLElement> {
  mobileNavLinks?: MobileNavLink[];
  footerNavLinks: FooterNavLink[];
}

interface FooterNavLink {
  name: string;
  href: string;
  icon: string;
}

function MobileSidebar({
  open,
  close,
  children,
}: React.PropsWithChildren<{ open: boolean; close: () => void }>) {
  return (
    <Headless.Transition show={open}>
      <Headless.Dialog onClose={close} className="lg:hidden">
        <Headless.TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Headless.TransitionChild>
        <Headless.TransitionChild
          enter="ease-in-out duration-300"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="ease-in-out duration-300"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <Headless.DialogPanel className="fixed inset-y-0 z-101 w-full max-w-80 p-2 transition">
            <div className="flex h-full flex-col rounded-lg bg-white shadow-2xs ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
              <div className="-mb-3 ml-auto px-4 pt-3">
                <Headless.CloseButton
                  as="button"
                  aria-label="Close navigation"
                  onClick={close}
                >
                  <XIcon data-slot="icon" className="h-6 w-6" />
                </Headless.CloseButton>
              </div>
              {children}
            </div>
          </Headless.DialogPanel>
        </Headless.TransitionChild>
      </Headless.Dialog>
    </Headless.Transition>
  );
}

export function MobileNav({
  className,
  mobileNavLinks,
  footerNavLinks,
}: MobileNavProps) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  return (
    <>
      <div className="pt-2">
        <button
          className={cn(
            "relative z-40 -m-2 inline-flex items-center rounded-lg border border-zinc-600 stroke-zinc-900 p-1 text-black hover:bg-zinc-200/50 hover:stroke-zinc-600 focus-visible:outline-emerald-500 active:stroke-zinc-900 dark:stroke-zinc-100 dark:text-white dark:hover:bg-zinc-800/50 dark:hover:stroke-zinc-500 dark:active:stroke-zinc-100 sm:p-2",
            className,
          )}
          onClick={() => setIsMobileOpen(true)}
        >
          <TextIcon data-slot="icon" className="h-6 w-6 rotate-180" />
        </button>
      </div>
      <MobileSidebar open={isMobileOpen} close={() => setIsMobileOpen(false)}>
        <div className="mb-8 flex items-center px-4 pt-4">
          <RIVVALLogo className="w-36 text-black dark:text-white" />
        </div>
        <div className="space-y-4 px-4">
          {mobileNavLinks?.map((link, key) =>
            link.subNav ? (
              <Accordion type="single" collapsible key={`mobile-nav-${key}`}>
                <AccordionItem value={`item-${key}`} className="border-0">
                  <AccordionTrigger className="py-0 text-base leading-7 tracking-tight text-zinc-700 dark:text-zinc-300">
                    {link.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pl-4">
                      {link.subNav.map((subLink, subKey) => (
                        <Link
                          key={`mobile-subnav-${key}-${subKey}`}
                          href={subLink.href}
                          className="block text-sm leading-7 tracking-tight text-zinc-600 dark:text-zinc-400"
                          onClick={() => setIsMobileOpen(false)}
                        >
                          {subLink.title}
                        </Link>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ) : (
              <Link
                key={`mobile-nav-${key}`}
                href={link.href}
                className="block text-base leading-7 tracking-tight text-zinc-700 dark:text-zinc-300"
                onClick={() => setIsMobileOpen(false)}
              >
                {link.title}
              </Link>
            ),
          )}
        </div>
        <div className="mt-4 flex justify-center space-x-4 text-muted-foreground">
          {footerNavLinks.map(
            (item: { name: string; href: string; icon: string }) => (
              <Link
                key={`${item.href}-sub-nav-socials`}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between py-2 font-medium"
              >
                {(() => {
                  const IconComponent =
                    item.icon && item.icon in Icons
                      ? (Icons as IconsType)[item.icon as IconName]
                      : null;
                  return IconComponent ? (
                    <IconComponent className="mb-2 h-6 w-6" />
                  ) : null;
                })()}
              </Link>
            ),
          )}
        </div>
      </MobileSidebar>
    </>
  );
}
